"use client";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Truck, Ship, FileText } from "lucide-react";
import { cn } from "@/lib/utils";
import { useState } from "react";
import { ShippingType } from "@/types/models/order";

interface ShippingTypeSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (type: ShippingType) => void;
}

export function ShippingTypeSelectionModal({
  isOpen,
  onClose,
  onSelect,
}: ShippingTypeSelectionModalProps) {
  const [selectedType, setSelectedType] = useState<ShippingType>(
    ShippingType.INTERNAL,
  );

  const handleSelect = () => {
    if (selectedType) {
      onSelect(selectedType);
      setSelectedType(ShippingType.INTERNAL); // Reset to default after selection
    }
  };

  const handleClose = () => {
    setSelectedType(ShippingType.INTERNAL); // Reset to default on close
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl bg-card border border-border/40 shadow-2xl">
        <DialogHeader className="pb-4 border-b border-border/60">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-primary/10 rounded-md">
              <Ship className="h-4 w-4 text-primary" />
            </div>
            <DialogTitle className="text-lg font-semibold text-foreground">
              Choose Shipping Method
            </DialogTitle>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Select how you want to ship this order set
          </p>
        </DialogHeader>

        <div className="py-6 space-y-4">
          {/* Shiphouse Option */}
          <button
            onClick={() => setSelectedType(ShippingType.INTERNAL)}
            className={cn(
              "w-full p-6 rounded-lg border-2 text-left transition-all",
              selectedType === ShippingType.INTERNAL
                ? "border-primary bg-primary/5"
                : "border-border hover:border-primary/50 hover:bg-muted/30",
            )}
          >
            <div className="flex items-start gap-4">
              <div
                className={cn(
                  "p-3 rounded-lg",
                  selectedType === ShippingType.INTERNAL
                    ? "bg-primary/10"
                    : "bg-muted",
                )}
              >
                <Truck
                  className={cn(
                    "h-6 w-6",
                    selectedType === ShippingType.INTERNAL
                      ? "text-primary"
                      : "text-muted-foreground",
                  )}
                />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-base font-semibold text-foreground">
                    Shiphouse Shipping
                  </h3>
                  {selectedType === ShippingType.INTERNAL && (
                    <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                      <svg
                        className="w-3 h-3 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mb-3">
                  Use our integrated shipping partners for automated tracking
                  and label generation
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-2 py-1 text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded-md">
                    Automated Tracking
                  </span>
                  <span className="px-2 py-1 text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 rounded-md">
                    Real-time Updates
                  </span>
                  <span className="px-2 py-1 text-xs bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 rounded-md">
                    Label Generation
                  </span>
                </div>
              </div>
            </div>
          </button>

          {/* Manual Shipping Option */}
          <button
            onClick={() => setSelectedType(ShippingType.EXTERNAL)}
            className={cn(
              "w-full p-6 rounded-lg border-2 text-left transition-all",
              selectedType === ShippingType.EXTERNAL
                ? "border-primary bg-primary/5"
                : "border-border hover:border-primary/50 hover:bg-muted/30",
            )}
          >
            <div className="flex items-start gap-4">
              <div
                className={cn(
                  "p-3 rounded-lg",
                  selectedType === ShippingType.EXTERNAL
                    ? "bg-primary/10"
                    : "bg-muted",
                )}
              >
                <FileText
                  className={cn(
                    "h-6 w-6",
                    selectedType === ShippingType.EXTERNAL
                      ? "text-primary"
                      : "text-muted-foreground",
                  )}
                />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-base font-semibold text-foreground">
                    Manual Shipping
                  </h3>
                  {selectedType === ShippingType.EXTERNAL && (
                    <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                      <svg
                        className="w-3 h-3 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mb-3">
                  Use your own courier service and enter tracking details
                  manually
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-2 py-1 text-xs bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 rounded-md">
                    Manual Tracking
                  </span>
                  <span className="px-2 py-1 text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 rounded-md">
                    Own Courier
                  </span>
                  <span className="px-2 py-1 text-xs bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400 rounded-md">
                    Manual Updates
                  </span>
                </div>
              </div>
            </div>
          </button>
        </div>

        <div className="flex items-center justify-end gap-2 pt-4 border-t border-border/60">
          <Button
            variant="outline"
            onClick={handleClose}
            className="text-sm h-9 px-4"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSelect}
            disabled={!selectedType}
            className="text-sm h-9 px-4 bg-primary hover:bg-primary/90"
          >
            Continue
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
